import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Chip } from '@mui/material';
import { FaEllipsisV } from 'react-icons/fa';
import { useProjectPermissions } from '../../../hooks/useProjectPermissions';
import { COLORS } from '../../../constants/radar';
import { getStatusColor, getStatusTextColor, getStatusText } from '../../../constants/projectStatuses';
import { getScopeBackgroundColor, getScopeTextColor, getScopeName } from '../../../constants/projectScopeTypes';
import { getClientPartyTypeName, getClientPartyTypeBackgroundColor, getClientPartyTypeTextColor } from '../../../constants/clientPartyTypes';
import { shouldHideAdvancedFeatures } from '../../../constants/projectPurposes';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import {
  ProjectCard,
  ProjectCardContent,
  ProjectTitle,
  ProjectCode,
  ProjectDetailsGrid,
  DashboardsSection,
  DashboardsTitle,
  ItalicText,
} from '../styles';
import { toLocalDate } from '../../../utils/dateOnly';

const ProjectCardItem = ({ project, onClick, onView, onEdit, onManageEmployees, onManageCode, onDelete }) => {
  const { canManageProject, canManageSpecialProjects, canUpdateProjectCode } = useProjectPermissions();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleViewClick = () => {
    handleMenuClose();
    if (onView) onView(project);
  };

  const handleEditClick = () => {
    handleMenuClose();
    if (onEdit) onEdit(project);
  };

  const handleManageEmployeesClick = () => {
    handleMenuClose();
    if (onManageEmployees) onManageEmployees(project);
  };

  const handleManageCodeClick = () => {
    handleMenuClose();
    if (onManageCode) onManageCode(project);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    if (onDelete) onDelete(project);
  };

  const handleCardClick = () => {
    // If user can manage project, navigate to edit mode
    // Otherwise, navigate to view mode
    if (canManageProject(project)) {
      if (onClick) {
        onClick(project);
      }
    } else {
      if (onView) {
        onView(project);
      }
    }
  };

  return (
    <ProjectCard key={project.id} onClick={handleCardClick}>
      <ProjectCardContent>
        <ProjectTitle>
          {project.name}
          <IconButton
            size="small"
            onClick={handleMenuOpen}
          >
            <FaEllipsisV />
          </IconButton>
        </ProjectTitle>
        <ProjectCode>
          <strong>Code:</strong>
          <span style={{ marginLeft: '4px' }}></span>
          {project.code ? <span>{project.code}</span> : <ItalicText>Not assigned</ItalicText>}
        </ProjectCode>
        <div style={{ textAlign: 'right', marginTop: '4px' }}>
          <div style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '16px',
            backgroundColor: '#E8F0FE',
            color: '#1967D2',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {project.businessArea?.name || '-'}
          </div>
        </div>
        <ProjectDetailsGrid>
          <div style={{ display: 'contents' }}>
            <strong>Business Unit:</strong>
            <div>
              {project.businessUnit ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span>{project.businessUnit.name}</span>
                  {project.businessUnit.cluster && (
                    <span style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      backgroundColor: '#F3F4F6',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {project.businessUnit.cluster.name}
                    </span>
                  )}
                  {project.businessUnit.orgUnitType && (
                    <span style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      backgroundColor: '#F3F4F6',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {project.businessUnit.orgUnitType.name}
                    </span>
                  )}
                </div>
              ) : (
                <ItalicText>Not assigned</ItalicText>
              )}
            </div>
          </div>

          <div style={{ display: 'contents' }}>
            <strong>Client:</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span>{project.client?.name || '-'}</span>
              {project.client?.partyType && (
                <Chip
                  label={getClientPartyTypeName(project.client.partyType)}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '10px',
                    fontWeight: 500,
                    backgroundColor: getClientPartyTypeBackgroundColor(project.client.partyType),
                    color: getClientPartyTypeTextColor(project.client.partyType),
                    '& .MuiChip-label': {
                      px: 0.5
                    }
                  }}
                />
              )}
            </div>
          </div>

          <div style={{ display: 'contents' }}>
            <strong>Duration:</strong>
            <div>
              {project.startDate ? (
                <>
                  {`${toLocalDate(project.startDate)?.toLocaleDateString()} - ${project.endDate ?
                    toLocalDate(project.endDate)?.toLocaleDateString() :
                    ''}`}
                  {!project.endDate && (
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      backgroundColor: '#E6F4EA',
                      color: '#1E8E3E',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginLeft: '4px'
                    }}>
                      ongoing
                    </span>
                  )}
                </>
              ) : '-'}
            </div>
          </div>

          <div style={{ display: 'contents' }}>
            <strong>Employees:</strong>
            <div>
              {project.employees ? (
                <>
                  {project.employees.total} active ({project.employees.engaged} engaged, {project.employees.leaving} leaving)
                  {project.employees.joining > 0 && `, ${project.employees.joining} joining`}
                </>
              ) : '-'}
            </div>
          </div>

          <div style={{ display: 'contents', alignItems: 'center' }}>
            <strong style={{ alignSelf: 'center' }}>Representative:</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {project.manager ? (
                <>
                  <EmployeeAvatar
                    employee={project.manager}
                    size={24}
                  />
                  {project.manager.fullName}
                </>
              ) : (
                <ItalicText>Not assigned</ItalicText>
              )}
            </div>
          </div>

          {project.scope && (
            <div style={{ display: 'contents' }}>
              <strong style={{ alignSelf: 'center' }}>Scope:</strong>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  label={getScopeName(project.scope)}
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '12px',
                    backgroundColor: getScopeBackgroundColor(project.scope),
                    color: getScopeTextColor(project.scope),
                    fontWeight: 500
                  }}
                />
              </div>
            </div>
          )}

          {project.projectPurpose && (
            <div style={{ display: 'contents' }}>
              <strong style={{ alignSelf: 'center' }}>Purpose:</strong>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  label={project.projectPurpose.name}
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '12px',
                    backgroundColor: getScopeBackgroundColor(project.scope),
                    color: getScopeTextColor(project.scope),
                    fontWeight: 500
                  }}
                />
              </div>
            </div>
          )}
        </ProjectDetailsGrid>
        {!shouldHideAdvancedFeatures(project.projectPurpose) && (
          <DashboardsSection>
            <DashboardsTitle>
              Dashboards ({project.numberOfDashboards})
            </DashboardsTitle>
          </DashboardsSection>
        )}
      </ProjectCardContent>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleViewClick}>
          View
        </MenuItem>
        {canManageProject(project) && (
          <MenuItem onClick={handleEditClick}>
            Edit
          </MenuItem>
        )}
        {canManageSpecialProjects(project) && (
          <MenuItem onClick={handleManageEmployeesClick}>
            Manage Individuals
          </MenuItem>
        )}
        {canUpdateProjectCode(project) && (
          <MenuItem onClick={handleManageCodeClick}>
            Manage Code
          </MenuItem>
        )}
        {canManageProject(project) && (
          <MenuItem
            onClick={handleDeleteClick}
            sx={{
              color: COLORS.DESTRUCTIVE,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </ProjectCard>
  );
};

export default ProjectCardItem;
